import { useState } from 'react';
import { Box, ButtonGroup, Divider, IconButton, Tooltip } from '@mui/material';
import { ContentCopy, ContentCut, ContentPaste, SelectAll, Undo, Clear, Flip, RotateLeft, RotateRight } from '@mui/icons-material';
import { 
  normalizeSelection, 
  extractSelectionData, 
  flipDataHorizontally,
  flipDataVertically,
  rotateDataClockwise,
  rotateDataCounterClockwise,
  replaceSelectionData
} from '../gridUtils';
import { useChartData } from './ChartDataProvider';

const EditPanel = () => {
  const { 
    data, 
    rows, 
    cols, 
    isSelect, 
    selection,
    actions 
  } = useChartData();
  
  const [clipboard, setClipboard] = useState(null);

  // Clear the grid
  const handleClear = () => {
    actions.updateData(Array.from({ length: rows }, () => Array.from({ length: cols }, () => 0)));
  };

  // Reset the grid
  const handleReset = () => {
    actions.resetData();
    setClipboard(null);
  };

  // Copy the selected area or the whole chart
  const handleCopy = () => {
    if (selection) {
      const normalized = normalizeSelection(selection);
      const copiedData = extractSelectionData(data, selection);
      
      setClipboard(copiedData);
      console.log(`Selected area copied: ${normalized.maxRow - normalized.minRow + 1}x${normalized.maxCol - normalized.minCol + 1} cells`);
    } else {
      setClipboard(data);
      console.log('Entire chart copied');
    }
  };

  // Cut the selected area: copy it to the clipboard and clear it from the chart
  const handleCut = () => {
    if (selection) {
      const normalized = normalizeSelection(selection);
      const copiedData = extractSelectionData(data, selection);
      
      setClipboard(copiedData);

      const newData = data.map((row, rowIndex) =>
        row.map((cell, colIndex) => {
          if (rowIndex >= normalized.minRow && rowIndex <= normalized.maxRow && 
              colIndex >= normalized.minCol && colIndex <= normalized.maxCol) {
            return 0; // Clear the cell
          }
          return cell;
        })
      );
      actions.updateData(newData);
      console.log(`Selected area cut: ${normalized.maxRow - normalized.minRow + 1}x${normalized.maxCol - normalized.minCol + 1} cells`);
    } else {
      console.log('No selection to cut');
    }
  };

  // Paste the clipboard data into the chart
  const handlePaste = () => {
    if (!clipboard) {
      console.log('No chart copied to paste');
      return;
    }
    
    // Since we always use standard orientation, no transformation needed
    const dataToApply = clipboard;
    
    // Determine where to paste: top-left of the selection or (0, 0)
    let pasteRow = 0;
    let pasteCol = 0;
    
    if (selection) {
      // Normalize selection coordinates and use top-left corner
      const normalized = normalizeSelection(selection);
      pasteRow = normalized.minRow;
      pasteCol = normalized.minCol;
    }

    const clipboardRows = dataToApply.length;
    const clipboardCols = dataToApply[0] ? dataToApply[0].length : 0;
    
    // Check if paste would go out of bounds
    if (pasteRow + clipboardRows > rows || pasteCol + clipboardCols > cols) {
      console.log(`Cannot paste: would exceed chart bounds. Clipboard size: ${clipboardRows}x${clipboardCols}, Paste position: ${pasteRow + 1},${pasteCol + 1}, Chart size: ${rows}x${cols}`);
      return;
    }

    const newData = data.map((row, rowIndex) =>
      row.map((cell, colIndex) => {
        // Determine where to paste the clipboard content
        const clipboardRow = rowIndex - pasteRow;
        const clipboardCol = colIndex - pasteCol;

        // Check if the current cell falls within the clipboard boundaries
        if (clipboardRow >= 0 && clipboardRow < clipboardRows && 
            clipboardCol >= 0 && clipboardCol < clipboardCols) {
          return dataToApply[clipboardRow][clipboardCol];
        }
        return cell; // Keep the existing cell value if outside the clipboard area
      })
    );
    console.log(`Chart pasted at row ${pasteRow + 1}, column ${pasteCol + 1} (${clipboardRows}x${clipboardCols} cells, transformed from standard orientation)`);
    actions.updateData(newData);
  };

  const handleOnSelect = () => {
    actions.setSelection(null);
    actions.setIsSelect(!isSelect);
    setClipboard(null);
  };

  // Flip the chart or selection horizontally
  const handleFlipHorizontally = () => {
    if (selection) {
      const selectionData = extractSelectionData(data, selection);
      const flippedData = flipDataHorizontally(selectionData);
      const newData = replaceSelectionData(data, flippedData, selection);
      actions.updateData(newData);
      console.log('Selected area flipped horizontally');
    } else {
      // Flip the entire chart horizontally
      const newData = flipDataHorizontally(data);
      actions.updateData(newData);
      console.log('Entire chart flipped horizontally');
    }
  };

  // Flip the chart or selection vertically
  const handleFlipVertically = () => {
    if (selection) {
      const selectionData = extractSelectionData(data, selection);
      const flippedData = flipDataVertically(selectionData);
      const newData = replaceSelectionData(data, flippedData, selection);
      actions.updateData(newData);
      console.log('Selected area flipped vertically');
    } else {
      // Flip the entire chart vertically
      const newData = flipDataVertically(data);
      actions.updateData(newData);
      console.log('Entire chart flipped vertically');
    }
  };

  // Rotate the chart or selection 90 degrees clockwise (right)
  const handleRotateRight = () => {
    if (selection) {
      const selectionData = extractSelectionData(data, selection);
      const rotatedData = rotateDataClockwise(selectionData);
      
      // Check if rotated selection would fit within chart bounds
      const normalized = normalizeSelection(selection);
      const selectionHeight = normalized.maxRow - normalized.minRow + 1;
      const selectionWidth = normalized.maxCol - normalized.minCol + 1;
      
      if (normalized.minRow + selectionWidth > rows || normalized.minCol + selectionHeight > cols) {
        console.log('Cannot rotate: rotated selection would exceed chart bounds');
        return;
      }
      
      const newData = replaceSelectionData(data, rotatedData, selection);
      actions.updateData(newData);
      console.log('Selected area rotated 90° clockwise');
    } else {
      // Rotate the entire chart
      const rotatedData = rotateDataClockwise(data);
      actions.updateData(rotatedData);
      console.log(`Entire chart rotated 90° clockwise (${rows}x${cols} → ${cols}x${rows})`);
    }
  };

  // Rotate the chart or selection 90 degrees counter-clockwise (left)
  const handleRotateLeft = () => {
    if (selection) {
      const selectionData = extractSelectionData(data, selection);
      const rotatedData = rotateDataCounterClockwise(selectionData);
      
      // Check if rotated selection would fit within chart bounds
      const normalized = normalizeSelection(selection);
      const selectionHeight = normalized.maxRow - normalized.minRow + 1;
      const selectionWidth = normalized.maxCol - normalized.minCol + 1;
      
      if (normalized.minRow + selectionWidth > rows || normalized.minCol + selectionHeight > cols) {
        console.log('Cannot rotate: rotated selection would exceed chart bounds');
        return;
      }
      
      const newData = replaceSelectionData(data, rotatedData, selection);
      actions.updateData(newData);
      console.log('Selected area rotated 90° counter-clockwise');
    } else {
      // Rotate the entire chart
      const rotatedData = rotateDataCounterClockwise(data);
      actions.updateData(rotatedData);
      console.log(`Entire chart rotated 90° counter-clockwise (${rows}x${cols} → ${cols}x${rows})`);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <ButtonGroup size="small">
        <Tooltip title="Undo All">
          <IconButton onClick={handleReset}>
            <Undo />
          </IconButton>
        </Tooltip>
        <Tooltip title="Clear All">
          <IconButton onClick={handleClear}>
            <Clear />
          </IconButton>
        </Tooltip>
      </ButtonGroup>
      <Divider orientation="vertical" flexItem />
      <ButtonGroup size="small">
        <Tooltip title="Selection Mode">
          <IconButton 
            aria-pressed={isSelect} 
            onClick={handleOnSelect}
            sx={{ 
              bgcolor: isSelect ? 'primary.main' : 'transparent',
              color: isSelect ? 'white' : 'inherit',
              '&:hover': {
                bgcolor: isSelect ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <SelectAll />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy">
          <IconButton onClick={handleCopy}>
            <ContentCopy />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cut">
          <IconButton onClick={handleCut} disabled={selection === null}>
            <ContentCut />
          </IconButton>
        </Tooltip>
        <Tooltip title="Paste">
          <IconButton onClick={handlePaste} disabled={clipboard === null}>
            <ContentPaste />
          </IconButton>
        </Tooltip>
      </ButtonGroup>
      <Divider orientation="vertical" flexItem />
      <ButtonGroup size="small">
        <Tooltip title="Flip Horizontally">
          <IconButton onClick={handleFlipHorizontally}>
            <Flip />
          </IconButton>
        </Tooltip>
        <Tooltip title="Flip Vertically">
          <IconButton onClick={handleFlipVertically}>
            <Flip sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>
        </Tooltip>
      </ButtonGroup>
      <Divider orientation="vertical" flexItem />
      <ButtonGroup size="small">
        <Tooltip title="Rotate Left (90° Counter-Clockwise)">
          <IconButton onClick={handleRotateLeft}>
            <RotateLeft />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rotate Right (90° Clockwise)">
          <IconButton onClick={handleRotateRight}>
            <RotateRight />
          </IconButton>
        </Tooltip>
      </ButtonGroup>
    </Box>
  );
};

export default EditPanel;
